import { CARD_HEIGHT } from '../constants/card-layout';

/**
 * Returns the id of a root entry (standalone card OR category) to insert
 * *before*, so the dropped card lands at the cursor's vertical position
 * instead of always appending to the end.
 *
 *  1. If the cursor is directly over a root standalone card
 *     (`data-hospital-root-card-id`), insert before that card.
 *  2. If the cursor is over a category panel (`data-category-id`), insert
 *     before that category.
 *  3. Otherwise (cursor in the empty gap between root entries), find the
 *     first root entry whose top edge is below the cursor and insert before
 *     it. This is what makes dropping in the GAP between two categories
 *     land the new standalone card at that gap rather than at the very end.
 */
export function getStandaloneInsertBeforeIdFromPoint(pt, draggedCardId) {
	if (!pt || draggedCardId == null) return null;

	const el = document.elementFromPoint(pt.x, pt.y);

	// (1) cursor on a standalone root card
	const rootCard = el?.closest?.('[data-hospital-root-card-id]');
	if (rootCard) {
		const id = rootCard.getAttribute('data-hospital-root-card-id');
		if (id && id !== draggedCardId) return id;
	}

	// (2) cursor on a category panel
	const category = el?.closest?.('[data-category-id]');
	if (category) {
		const id = category.getAttribute('data-category-id');
		if (id && id !== draggedCardId) return id;
	}

	// (3) cursor in empty space — pick the first root entry whose top edge
	// is at or below the cursor.
	const root = document.querySelector("[data-hospital-root-layout='1']");
	if (!root) return null;

	let bestId = null;
	let bestTop = Infinity;
	const consider = (node) => {
		if (!node) return;
		const id =
			node.getAttribute?.('data-hospital-root-card-id') ||
			node.getAttribute?.('data-rgl-item-id') ||
			node.querySelector?.('[data-category-id]')?.getAttribute('data-category-id');
		if (!id || id === draggedCardId) return;
		const r = node.getBoundingClientRect();
		if (r.top >= pt.y && r.top < bestTop) {
			bestTop = r.top;
			bestId = id;
		}
	};
	// Direct children of the root grid first (the cells), then any
	// data-rgl-item-id descendants as a backup if the cell wrapper is one
	// level deeper.
	for (const child of Array.from(root.children)) consider(child);
	if (!bestId) {
		for (const cell of Array.from(root.querySelectorAll('[data-rgl-item-id]'))) consider(cell);
	}
	return bestId;
}

/**
 * When dropping a card onto a card INSIDE a category, find the id of the hovered card
 * so we can insert the incoming card at that position (shifting the hovered card and its
 * neighbors to the right), rather than appending to the end of the category.
 *
 * Category inner grids are rendered with `className="hospital-category-inner-dnd"` and each
 * inner cell carries `data-rgl-item-id`. Returns `{ categoryId, insertBeforeId }` when the
 * pointer is over a card cell inside a category; otherwise returns null.
 *
 * Why this is not a simple `elementFromPoint`:
 *  - During and right after a drop, the dragged card's outer wrapper can still be stacked
 *    above the target at the drop coordinates (especially when RGL re-enables pointer-events
 *    between the `onDragStop` fire and the React commit). A naive `elementFromPoint` then
 *    returns the dragged card itself (outside any category inner grid) and we'd fall back to
 *    appending — which is exactly the bug this helper is meant to fix.
 *  - We therefore use `elementsFromPoint` (walks the full z-stack) and, as a final fallback,
 *    a bounding-rect hit test scoped to the already-resolved category so geometry wins even
 *    when hit-testing fails entirely.
 *
 * @param {object} options.categoryId Optional expected category id (e.g. the tracked
 *   `hoveredCategoryId`). When provided, the search is constrained to that category so a
 *   pointer briefly over the wrong category chrome doesn't resolve to the wrong target,
 *   and the geometry fallback has a scope to search within.
 */
export function getCategoryInsertBeforeTargetFromPoint(pt, draggedCardId, options = {}) {
	if (!pt || draggedCardId == null) return null;
	const expectedCategoryId = options.categoryId || null;

	const resolveFromElement = (el) => {
		if (!el || typeof el.closest !== 'function') return null;
		const innerContainer = el.closest('.hospital-category-inner-dnd');
		if (!innerContainer) return null;
		const categoryHost = innerContainer.closest('[data-category-id]');
		const categoryId = categoryHost?.getAttribute('data-category-id') || null;
		if (!categoryId) return null;
		if (expectedCategoryId && categoryId !== expectedCategoryId) return null;
		const cell = el.closest('[data-rgl-item-id]');
		if (!cell || !innerContainer.contains(cell)) {
			return { categoryId, insertBeforeId: null };
		}
		const id = cell.getAttribute('data-rgl-item-id');
		if (!id || id === draggedCardId) {
			return { categoryId, insertBeforeId: null };
		}
		return { categoryId, insertBeforeId: id };
	};

	const elements =
		typeof document.elementsFromPoint === 'function'
			? document.elementsFromPoint(pt.x, pt.y)
			: [document.elementFromPoint(pt.x, pt.y)].filter(Boolean);

	for (const el of elements) {
		const cellHere = el?.closest?.('[data-rgl-item-id]');
		if (cellHere && cellHere.getAttribute('data-rgl-item-id') === draggedCardId) continue;
		const resolved = resolveFromElement(el);
		if (resolved) return resolved;
	}

	if (expectedCategoryId) {
		const escape =
			typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function'
				? window.CSS.escape
				: (s) => s;
		const categoryHost = document.querySelector(
			`[data-category-id="${escape(expectedCategoryId)}"]`
		);
		const innerContainer = categoryHost?.querySelector('.hospital-category-inner-dnd');
		if (innerContainer) {
			const cells = innerContainer.querySelectorAll('[data-rgl-item-id]');
			for (const cell of cells) {
				const id = cell.getAttribute('data-rgl-item-id');
				if (!id || id === draggedCardId) continue;
				const r = cell.getBoundingClientRect();
				if (pt.x >= r.left && pt.x <= r.right && pt.y >= r.top && pt.y <= r.bottom) {
					return { categoryId: expectedCategoryId, insertBeforeId: id };
				}
			}
			return { categoryId: expectedCategoryId, insertBeforeId: null };
		}
	}

	return null;
}

/** Returns a new layout after moving one card, or null if the card was not found. */
export function computeLayoutAfterCardMove(layout, cardId, toCategoryId, options = {}) {
	if (!layout) return null;
	const standaloneInsertBeforeId = options.standaloneInsertBeforeId ?? null;
	let movingCard = null;
	const updatedStandalone = layout.standalone.filter((c) => {
		if (c.id === cardId) {
			movingCard = c;
			return false;
		}
		return true;
	});
	let updatedCategories = layout.categories.map((cat) => {
		const cardIndex = cat.cards.findIndex((c) => c.id === cardId);
		if (cardIndex !== -1) {
			movingCard = cat.cards[cardIndex];
			// Renumber so the source category's `position` fields stay consistent
			// with array order. Otherwise gaps left by removal can later collide
			// with stale positions on cards moved IN, producing wrong sort order.
			const remaining = cat.cards
				.filter((c) => c.id !== cardId)
				.map((c, i) => ({ ...c, position: i }));
			return { ...cat, cards: remaining };
		}
		return cat;
	});
	if (!movingCard) return null;
	if (toCategoryId === 'standalone' || !toCategoryId) {
		const standSans = updatedStandalone;
		const newStandalone = [...standSans];
		let ins = newStandalone.length;
		if (standaloneInsertBeforeId) {
			const j = newStandalone.findIndex((c) => c.id === standaloneInsertBeforeId);
			if (j !== -1) ins = j;
		}
		newStandalone.splice(ins, 0, movingCard);

		const catById = Object.fromEntries(updatedCategories.map((c) => [c.id, c]));
		const standByIdAll = Object.fromEntries(newStandalone.map((s) => [s.id, s]));

		let order =
			Array.isArray(layout.rootOrder) && layout.rootOrder.length > 0
				? layout.rootOrder.map((e) => ({ ...e }))
				: [
						...updatedCategories.map((c) => ({ id: c.id, type: 'category', width: 12 })),
						...standSans.map((s) => ({ id: s.id, type: 'card', width: s.width || 4 })),
					];
		order = order.filter((e) => e.id !== cardId);
		const newEntry = {
			id: movingCard.id,
			type: 'card',
			width: movingCard.width || 4,
		};
		let insertAt = order.length;
		if (standaloneInsertBeforeId) {
			const i = order.findIndex((e) => e.id === standaloneInsertBeforeId);
			if (i !== -1) insertAt = i;
		}
		order.splice(insertAt, 0, newEntry);
		const rootOrder = order.map((e, i) => ({
			id: e.id,
			type: catById[e.id] ? 'category' : 'card',
			width: catById[e.id] ? 12 : standByIdAll[e.id]?.width || e.width || 4,
			position: i,
		}));

		return {
			...layout,
			standalone: newStandalone,
			categories: updatedCategories,
			rootOrder,
		};
	}
	const categoryInsertBeforeId = options.categoryInsertBeforeId ?? null;
	updatedCategories = updatedCategories.map((cat) => {
		if (cat.id !== toCategoryId) return cat;
		const cards = [...cat.cards];
		let insertAt = cards.length;
		if (categoryInsertBeforeId) {
			const j = cards.findIndex((c) => c.id === categoryInsertBeforeId);
			if (j !== -1) insertAt = j;
		}
		cards.splice(insertAt, 0, movingCard);
		// Renumber `position` to match array index. Without this, the moved
		// card keeps its stale `position` from the source list — when it ties
		// with or is lower than an existing card's position, `sortCardsByLayout`
		// places it in the wrong slot (intermittently appearing as a swap with
		// the last card instead of an append at the end).
		const renumbered = cards.map((c, i) => ({ ...c, position: i }));
		return { ...cat, cards: renumbered };
	});
	return { ...layout, standalone: updatedStandalone, categories: updatedCategories };
}

/**
 * Move `cardId` to the root level (standalone) at a specific position in
 * `rootOrder`. The target index is interpreted against the ORIGINAL rootOrder
 * (i.e. before the source card is removed) — the helper adjusts internally
 * when the source was a root card sitting before the target index.
 *
 * Returns a new layout, or `null` if the card was not found.
 */
export function computeLayoutAfterRootGapInsert(layout, cardId, rootOrderIndex) {
	if (!layout || cardId == null || typeof rootOrderIndex !== 'number') return null;

	let movingCard = null;
	const updatedStandalone = layout.standalone.filter((c) => {
		if (c.id === cardId) {
			movingCard = c;
			return false;
		}
		return true;
	});

	const updatedCategories = layout.categories.map((cat) => {
		const idx = cat.cards.findIndex((c) => c.id === cardId);
		if (idx === -1) return cat;
		movingCard = cat.cards[idx];
		const remaining = cat.cards
			.filter((c) => c.id !== cardId)
			.map((c, i) => ({ ...c, position: i }));
		return { ...cat, cards: remaining };
	});

	if (!movingCard) return null;

	const fromRootIndex = Array.isArray(layout.rootOrder)
		? layout.rootOrder.findIndex((e) => e.id === cardId)
		: -1;

	let order = Array.isArray(layout.rootOrder) && layout.rootOrder.length > 0
		? layout.rootOrder.map((e) => ({ ...e }))
		: [
				...updatedCategories.map((c) => ({ id: c.id, type: 'category', width: 12 })),
				...updatedStandalone.map((s) => ({ id: s.id, type: 'card', width: s.width || 4 })),
			];
	order = order.filter((e) => e.id !== cardId);

	// Index given was computed against the array INCLUDING the source card.
	// If that source was a root-level card sitting before the target slot,
	// removal shifted everything after it left by one.
	let insertAt = rootOrderIndex;
	if (fromRootIndex !== -1 && fromRootIndex < insertAt) insertAt -= 1;
	insertAt = Math.max(0, Math.min(insertAt, order.length));

	order.splice(insertAt, 0, {
		id: movingCard.id,
		type: 'card',
		width: movingCard.width || 4,
	});

	// Keep the standalone array in lockstep with rootOrder so its ordering can
	// be relied on by other readers. Pull data from whichever source had the
	// moved card (standalone before the move, or the source category).
	const standDataById = new Map(layout.standalone.map((s) => [s.id, s]));
	standDataById.set(movingCard.id, movingCard);
	const catIds = new Set(updatedCategories.map((c) => c.id));
	const newStandalone = order
		.filter((e) => !catIds.has(e.id))
		.map((e) => standDataById.get(e.id))
		.filter(Boolean);

	const rootOrder = order.map((e, i) => ({
		id: e.id,
		type: catIds.has(e.id) ? 'category' : 'card',
		width: catIds.has(e.id) ? 12 : (standDataById.get(e.id)?.width || e.width || 4),
		position: i,
	}));

	return {
		...layout,
		standalone: newStandalone,
		categories: updatedCategories,
		rootOrder,
	};
}

export function categoryRootCellHeightPx(gridH, rowHeight = 10, marginY = 8) {
	if (gridH < 1) return 0;
	return gridH * rowHeight + Math.max(0, gridH - 1) * marginY;
}

export function resolveCategoryCardsForEstimate(category, cardDataList) {
	if (!category?.cards?.length || !cardDataList?.length) return [];
	return category.cards
		.map((cardItem) => {
			const def = cardDataList.find((c) => c.id === cardItem.id);
			return def ? { ...def, ...cardItem } : null;
		})
		.filter(Boolean);
}

/**
 * Maps persisted dashboard card `width` (in 12-col units) to inner category grid width.
 * When `compactTwoCol` is true (≤1280px desktop/tablet), small cards use 6 so two fit per row.
 */
export function innerGridDisplayWidth(storedWidth, compactTwoCol) {
	const w = storedWidth == null || storedWidth === 0 ? 4 : Number(storedWidth);
	if (!compactTwoCol) {
		return Math.min(12, Math.max(1, w));
	}
	if (w >= 12) return 12;
	if (w >= 8) return 12;
	return 6;
}

export function countCategoryInnerRowBands(cards, options = {}) {
	const { compactTwoCol = false } = options;
	if (!cards || cards.length === 0) return 0;
	const sorted = [...cards].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
	const cols = 12;
	let currentRow = 0;
	let currentCol = 0;
	let maxExtent = 0;
	const rowSpan = 24;
	for (const card of sorted) {
		const cardWidth = innerGridDisplayWidth(card.width, compactTwoCol);
		if (currentCol + cardWidth > cols) {
			currentRow += rowSpan;
			currentCol = 0;
		}
		const y = currentRow;
		maxExtent = Math.max(maxExtent, y + rowSpan);
		currentCol += cardWidth;
		if (currentCol >= cols) {
			currentRow += rowSpan;
			currentCol = 0;
		}
	}
	return Math.max(1, maxExtent / rowSpan);
}

/**
 * Smallest gridH so the root cell fits category chrome + inner DragDropLayout (full CARD_HEIGHT per row, h=2 items).
 */
export function estimateCategoryGridRowUnits(category, cardDataList, options = {}) {
	const { compactTwoCol = false } = options;
	const rowHeight = 10;
	const marginY = 8;
	const resolved = resolveCategoryCardsForEstimate(category, cardDataList);
	const n = resolved.length;
	const innerRows = n === 0 ? 0 : countCategoryInnerRowBands(resolved, { compactTwoCol });
	const chromePx = 76;
	const nestedRowPx = CARD_HEIGHT + marginY;
	// Empty categories need a taller root grid cell so the panel + drop zone match minHeight in CategoryBox.
	const innerBodyPx =
		n === 0 ? 300 : innerRows * nestedRowPx + Math.max(0, innerRows - 1) * marginY;
	const totalPx = chromePx + innerBodyPx + 4;

	let gridH = 1;
	while (categoryRootCellHeightPx(gridH, rowHeight, marginY) < totalPx && gridH < 300) {
		gridH += 1;
	}
	// Empty categories get a taller floor (~+100px in pixel space) so the
	// drop area is more visible and easier to target. Each grid unit ≈ 18px
	// (10 row + 8 margin), so 30 instead of 24 adds ~108px to the empty cell.
	const minH = n === 0 ? 30 : 24;
	const computed = Math.min(240, Math.max(minH, gridH));
	if (n === 0) return computed;
	if (innerRows === 1) return Math.min(computed, 40);
	return computed;
}

/** Pointer position from RGL/react-draggable stop event (mouse or last touch). */
export function getPointerClientCoords(e) {
	if (!e) return null;
	if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
		return { x: e.clientX, y: e.clientY };
	}
	const t = e.changedTouches?.[0] || e.touches?.[0];
	if (t && typeof t.clientX === 'number' && typeof t.clientY === 'number') {
		return { x: t.clientX, y: t.clientY };
	}
	return null;
}

export function pointInRect(pt, rect) {
	if (!pt || !rect) return false;
	return pt.x >= rect.left && pt.x <= rect.right && pt.y >= rect.top && pt.y <= rect.bottom;
}

/**
 * Which Hospital category `Paper` (`[data-category-id]`) should receive hover/drop
 * for this pointer position.
 *
 * We walk `elementsFromPoint` (with small probe offsets to escape drag ghosts) and
 * use `closest('[data-category-id]')`, but we only accept a category when the
 * **original** pointer `(clientX, clientY)` lies inside that Paper's border box
 * (`getBoundingClientRect`).
 *
 * Without the rect check, a tall chart/card that paints outside its category Paper
 * (`overflow: visible`) can still sit above the category below in the z-stack; its
 * DOM ancestors resolve to the **upper** category even when the cursor is visually
 * over the **lower** panel — wrong highlight and wrong drop target (often mistaken
 * for zoom / transform issues).
 */
export function resolveHospitalCategoryIdUnderPointer(clientX, clientY) {
	if (typeof clientX !== 'number' || typeof clientY !== 'number') return null;
	const pt = { x: clientX, y: clientY };
	const offsets = [[0, 0], [5, 0], [-5, 0], [0, 5], [0, -5], [8, 8], [-8, -8]];
	for (const [dx, dy] of offsets) {
		const sx = clientX + dx;
		const sy = clientY + dy;
		const stack =
			typeof document.elementsFromPoint === 'function'
				? document.elementsFromPoint(sx, sy)
				: [document.elementFromPoint(sx, sy)].filter(Boolean);
		for (const node of stack) {
			if (!node || typeof node.closest !== 'function') continue;
			if (node.classList?.contains('react-draggable-dragging')) continue;
			if (node.closest?.('.react-draggable-dragging')) continue;
			const catEl = node.closest('[data-category-id]');
			if (!catEl) continue;
			const r = catEl.getBoundingClientRect();
			if (pointInRect(pt, r)) {
				const id = catEl.getAttribute('data-category-id');
				if (id) return id;
			}
		}
	}
	return null;
}

/**
 * True if the pointer is over a root dashboard cell that is NOT inside a category.
 *
 * We must walk `elementsFromPoint` (plural) and skip both the currently-dragging
 * `.react-grid-item.react-draggable-dragging` and its descendants, because at
 * `onDragStop` time the dragged card is still positioned at the pointer with
 * `pointer-events` enabled — a naive `elementFromPoint` always hits that dragged
 * card (which is inside `.hospital-category-inner-dnd`) and wrongly concludes
 * that the drop is still "inside the category", blocking drag-out-of-category.
 */
export function pointerOverHospitalRootStandaloneArea(pt) {
	if (!pt) return false;
	const stack = typeof document.elementsFromPoint === 'function'
		? document.elementsFromPoint(pt.x, pt.y)
		: [document.elementFromPoint(pt.x, pt.y)];
	const el = (stack || []).find((node) => {
		if (!node) return false;
		if (node.classList?.contains('react-draggable-dragging')) return false;
		if (node.closest?.('.react-draggable-dragging')) return false;
		return true;
	});
	if (!el) return false;
	const root = el.closest("[data-hospital-root-layout='1']");
	if (!root) return false;
	// Anywhere inside an inner-category DnD grid → not standalone.
	if (el.closest('.hospital-category-inner-dnd')) return false;
	// Anywhere inside a category panel itself (header, padding, chrome) →
	// also not standalone. The new HTML5 DragDropLayout doesn't apply
	// `.react-grid-item` like the legacy RGL did, so we walk via the
	// `data-category-id` attribute that CategoryBox always sets on its
	// Paper, which covers the entire panel including chrome.
	if (el.closest('[data-category-id]')) return false;
	// (Legacy fallback for the RGL-era markup, harmless here.)
	const item = el.closest('.react-grid-item');
	if (item && item.querySelector('[data-category-id]')) return false;
	return true;
}
